import CheckoutSelectShippingMethod from '@features/CheckoutSelectShippingMethod';
import {
  useCheckoutCartState,
  useFeatureIsOn,
} from '@monofront/business/contexts';
import {
  ABTEST_MOBILE_SHIPPING_STICKY_CTA,
  CHECKOUT_TRACKING_STEPS,
} from '@monofront/business/constants';
import { IAddressV2, ICheckoutPageContent } from '@monofront/types';
import Button from '@monofront/design-system/components/Button';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import Divider from '@monofront/design-system/components/Divider';
import CheckoutShippingAddress from '@features/CheckoutShippingAddress';
import CheckoutBillingAddress from '@features/CheckoutBillingAddress';
import { Body, NextAction } from '@components/LayoutCheckout';
import Coupon from '@features/Coupon';
import React, { useEffect, useRef } from 'react';
import { dataLayerTrackEvent } from '@utils/tracking';
import { trackEvent } from '@monofront/tracking';
import CustomError from '@screens/CustomError';
import { RichText } from 'prismic-reactjs';
import logger from '@utils/logger';
import { ErrorBoundary } from 'react-error-boundary';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import {
  useCheckoutCartMachine,
  useCheckoutSelectAddressMachine,
} from '@context/checkoutCartMachineContext';
import { ISelectShippingMethodEvent } from '@monofront/business/stateMachines/checkout/types';
import { CHECKOUT_TAGS_LOADING } from '@monofront/business/stateMachines/checkout/tags';
import {
  SELECT_SHIPPING_METHOD_TRANSITIONS,
  SELECT_SHIPPING_METHOD_STATES,
  SELECT_ADDRESS_STATES,
} from '@monofront/business/stateMachines';
import {
  CHECKOUT_STATES,
  CHECKOUT_TRANSITIONS,
} from '@monofront/business/stateMachines/checkout';
import { SELECT_TIME_SLOTS_STATES } from '@monofront/business/stateMachines/checkout/selectShippingMethod/selectTimeSlots';
import { useSubscribeInCheckout } from '@monofront/business/hooks/checkout/useSubscribeInCheckout';
import { useAuthentication } from '@context/businessContainer';
import { triggerCustomerUpdate } from '@services/user';
import * as prismicH from '@prismicio/helpers';
import { toast } from 'react-toastify';
import useOnRoundUpChange from '@features/CheckoutRoundUp/useOnRoundUpChange';
import HeartLoader from '@monofront/design-system/components/HeartLoader/HeartLoader';
import { useTagContextMatches } from '@hooks/businessContainer';

interface ICheckoutDeliveryProps {
  pageContents?: Partial<ICheckoutPageContent>;
}

const CheckoutDelivery: React.FC<ICheckoutDeliveryProps> = ({
  pageContents,
}) => {
  const { isMember, customer, authUser } = useAuthentication();
  const { checkoutCart, error } = useCheckoutCartState();
  const { state, isWaitingForCart } = useCheckoutCartMachine();
  const {
    sendToSelectShippingMethod,
    selectAddressState,
    selectShippingMethodState,
    selectTimeSlotsState,
  } = useCheckoutSelectAddressMachine();
  const { isSubscriptionAvailable, isLoading } =
    useSubscribeInCheckout(useAuthentication);
  const trackingEventSent = useRef(false);
  const router = useRouter();
  const { tagContextMatches } = useTagContextMatches();

  const tCheckout = useTranslations('Checkout');
  const tShipping = useTranslations('Shipping');
  const customerUpdateHasBeenTriggered = useRef(false);
  const isMobileShippingStickyCtaABTestEnabled = useFeatureIsOn(
    ABTEST_MOBILE_SHIPPING_STICKY_CTA,
  );
  const isDeliveryWarningDisplayed =
    prismicH.asText(pageContents?.shippingWarningMessage) &&
    tagContextMatches(pageContents?.shippingWarningTagContext ?? '');

  const isNextButtonHidden =
    isMobileShippingStickyCtaABTestEnabled &&
    !state.can(CHECKOUT_TRANSITIONS.SELECT_PAYMENT_METHOD);

  useOnRoundUpChange(checkoutCart?.roundUpAmount || 0);

  //TODO do this in State Machine transition
  useEffect(() => {
    if (!trackingEventSent.current && checkoutCart && !isLoading) {
      const products = checkoutCart.items.map(
        ({ name, sku, quantity, unitPrice, productVendor }) => ({
          name,
          id: sku,
          quantity,
          price: unitPrice / 100,
          brand: productVendor,
        }),
      );

      // deprecated tracker https://gitlab.com/la-fourche/scrum/-/issues/1968
      dataLayerTrackEvent('checkout', {
        ecommerce: {
          checkout: {
            actionField: {
              step: CHECKOUT_TRACKING_STEPS.DELIVERY,
              option: {},
              isMaap: isSubscriptionAvailable,
            },
            products,
          },
        },
      });

      trackEvent({
        type: 'LFStartCheckout',
        data: {
          currency: checkoutCart.currencyCode,
          value: checkoutCart.subTotal / 100,
          isMaap: isSubscriptionAvailable,
          products: products.map((product) => ({
            ...product,
            sku: product.id,
            price: product.price * 100,
          })),
        },
      });

      trackingEventSent.current = true;
    }
  }, [checkoutCart, isSubscriptionAvailable, isLoading]);

  // TODO: do we still need any of these?
  useEffect(() => {
    if (checkoutCart?.withMembership === false && !isSubscriptionAvailable) {
      void (async function logShoppingWarning() {
        logger.warn(
          "Shopping notice shown, even though it shouldn't be available! Look for mismatch between JWT and current customer state",
          {
            user_id: customer?.uid,
            shopping_cart_id: customer?.shoppingCartId,
            isMember,
            omsTags: customer?.omsTags,
            token: await authUser?.getIdToken(),
          },
        );
      })();

      // Condition used to prevent infinite loop as customer is defined as dependency
      if (!customerUpdateHasBeenTriggered.current && customer) {
        triggerCustomerUpdate(customer.uid); // Force sync customer
        customerUpdateHasBeenTriggered.current = true;
      }
    }
  }, [checkoutCart, isSubscriptionAvailable, isMember, customer]);

  if (error) {
    return (
      <Body>
        <CustomError statusCode={404} />
      </Body>
    );
  }

  return (
    <>
      <Body>
        {/* TODO: refacto and use InformativeBox once removed from /Cart */}
        {isDeliveryWarningDisplayed ? (
          <ErrorBoundary
            fallbackRender={() => null}
            onError={(e) =>
              logger.error(
                'An error occurred while rendering checkout warning message',
                {
                  error: e,
                },
              )
            }
          >
            <div className="alert-info">
              {pageContents?.shippingWarningMessagePicto?.url && (
                <div className="alert-info-image">
                  <ResponsiveImage
                    src={pageContents.shippingWarningMessagePicto.url}
                    width={56}
                    height={31}
                    layout="fixed"
                  />
                </div>
              )}
              <RichText render={pageContents?.shippingWarningMessage} />
            </div>
          </ErrorBoundary>
        ) : null}
        <div className="delivery-body">
          <section>
            <h3 className="subtitle hxb">{tCheckout('myAddress')}</h3>
            <CheckoutShippingAddress />
          </section>
          {isWaitingForCart && <HeartLoader />}
          {state.matches(CHECKOUT_STATES.POST_ADDRESS_SELECTED) && (
            <>
              <Divider marginVertical="tw-my-6" />
              <HeartLoader />
            </>
          )}
          {state.can({
            type: CHECKOUT_TRANSITIONS.SELECT_SHIPPING_METHOD,
          } as ISelectShippingMethodEvent) && (
            <>
              <Divider marginVertical="tw-my-6" />
              <CheckoutSelectShippingMethod />
            </>
          )}
          {state.can({
            type: CHECKOUT_TRANSITIONS.SELECT_BILLING_ADDRESS,
            billingAddress: {} as IAddressV2,
          }) && (
            <>
              <Divider marginVertical="tw-my-6" />
              <section>
                <h3 className="subtitle hxb">{tCheckout('billingAddress')}</h3>
                <CheckoutBillingAddress />
              </section>
            </>
          )}
        </div>
        <div className="mobile-only">
          <Divider marginVertical="tw-my-6" />
          <Coupon />
        </div>
      </Body>
      <NextAction
        isMembershipSummaryDisplayed={!isLoading && isSubscriptionAvailable}
        isNextButtonHidden={isNextButtonHidden}
      >
        <Button
          onClick={() => {
            if (state.matches(CHECKOUT_STATES.STARTED)) {
              toast.error(tCheckout('enterAnAddress'));

              return;
            }

            if (state.matches(CHECKOUT_STATES.ADDRESSED)) {
              toast.error(tCheckout('selectAShippingMethod'));

              return;
            }

            if (state.matches(CHECKOUT_STATES.SHIPPING_SELECTED)) {
              toast.error(tCheckout('completeShippingMethod'));

              return;
            }

            if (state.can(CHECKOUT_TRANSITIONS.SELECT_PAYMENT_METHOD)) {
              sendToSelectShippingMethod(
                SELECT_SHIPPING_METHOD_TRANSITIONS.SEND_TO_PARENT,
              );

              router.push(`/checkout/payment`);
            } else {
              toast.error(tCheckout('anErrorOccuredCheckSelectedOptions'));
            }
          }}
          isLoading={
            state.hasTag(CHECKOUT_TAGS_LOADING) ||
            isWaitingForCart ||
            selectAddressState.matches({
              [SELECT_ADDRESS_STATES.GET]:
                SELECT_ADDRESS_STATES.ADDRESSES_FETCHING,
            }) ||
            selectAddressState.matches({
              [SELECT_ADDRESS_STATES.GET_WITH_FORM]:
                SELECT_ADDRESS_STATES.ADDRESSES_FETCHING,
            }) ||
            selectShippingMethodState.matches(
              SELECT_SHIPPING_METHOD_STATES.METHODS_FETCHING,
            ) ||
            selectTimeSlotsState.matches(
              SELECT_TIME_SLOTS_STATES.SLOTS_FETCHING,
            )
          }
          hasMinWidth={false}
          disabled={
            !checkoutCart ||
            !state.can(CHECKOUT_TRANSITIONS.SELECT_PAYMENT_METHOD)
          }
          dataTestId={`CheckoutDelivery-Button-continue-to-payment`}
        >
          {tShipping(
            isSubscriptionAvailable
              ? 'continueToPaymentAsProspect'
              : 'continueToPayment',
          )}
        </Button>
      </NextAction>
      <style jsx>{`
        .delivery-body :global(h3) {
          margin: var(--space-5) 0;
        }

        .delivery-body :global(Divider) {
          border: 0;
          border-bottom: var(--border);
          margin: var(--space-4) 0;
        }

        .delivery-body {
          counter-reset: countSubTitles;
        }

        .delivery-body :global(.subtitle) {
          margin: 0;
          margin-bottom: var(--space-5);
        }

        .delivery-body :global(.subtitle)::before {
          counter-increment: countSubTitles 1;
          content: counter(countSubTitles) '. ';
        }

        .non-prospect-notice {
          margin-bottom: var(--space-5);
        }

        .alert-info {
          display: flex;
          align-items: center;
          margin-bottom: var(--space-6);
        }

        .alert-info-image {
          margin-right: var(--space-3);
        }

         {
          /*  TODO: remove on this issue is done: https://gitlab.com/la-fourche/scrum/-/issues/1760 */
        }
        .alert-info-text :global(a) {
          transition: all 0.3s ease;
          color: var(--clr-link);
          text-decoration: underline;
          line-height: 1.375;
          cursor: pointer;
        }

        .alert-info-text :global(a):hover {
          opacity: var(--opacity-link-hover);
        }

        .alert-info :global(strong) {
          color: var(--clr-secondary-1);
        }
      `}</style>
    </>
  );
};

export default React.memo(CheckoutDelivery, () => true);
